import type { FormsListSortBy, FormsListSortDir } from "./formsListPreferences";
import { isFormListItemClosed } from "../forms/formMeta";
import { getFormSource } from "../forms/formSource";
import { sortFormsList } from "../forms/sortFormsList";
import type { FormListItemDto } from "../../types/formList";

export type FormStatusFilter = "all" | "open" | "closed";
export type FormSourceFilter = "all" | "native" | "yandex";

export interface FormListQueryState {
  search: string;
  status: FormStatusFilter;
  source: FormSourceFilter;
  organisationId: number | null;
  sortBy: FormsListSortBy;
  sortDir: FormsListSortDir;
}

export interface FormListStats {
  total: number;
  open: number;
  closed: number;
}

export function getFormListStats(forms: FormListItemDto[]): FormListStats {
  let open = 0;
  let closed = 0;

  for (const form of forms) {
    if (isFormListItemClosed(form)) closed += 1;
    else open += 1;
  }

  return { total: forms.length, open, closed };
}

export function filterFormsList(
  forms: FormListItemDto[],
  filters: Pick<
    FormListQueryState,
    "search" | "status" | "source" | "organisationId"
  >,
): FormListItemDto[] {
  const query = filters.search.trim().toLowerCase();

  return forms.filter((form) => {
    const closed = isFormListItemClosed(form);

    if (filters.status === "open" && closed) return false;
    if (filters.status === "closed" && !closed) return false;

    if (filters.source === "native" && getFormSource(form) !== "native") {
      return false;
    }
    if (filters.source === "yandex" && getFormSource(form) !== "yandex") {
      return false;
    }

    if (
      filters.organisationId != null &&
      form.organisationId !== filters.organisationId
    ) {
      return false;
    }

    if (!query) return true;

    const haystack = [form.name, form.description ?? "", String(form.id)]
      .join(" ")
      .toLowerCase();

    return haystack.includes(query);
  });
}

export function applyFormListQuery(
  forms: FormListItemDto[],
  query: FormListQueryState,
): FormListItemDto[] {
  const filtered = filterFormsList(forms, query);
  return sortFormsList(filtered, query.sortBy, query.sortDir);
}
